import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Plus, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Textarea } from '@/components/ui/textarea';
import { UserManagement } from '@/components/UserManagement';
import { PageHeader } from '@/components/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Settings = () => {
  const { user, condominiums, selectedCondominium, refreshCondominiums } = useAuth();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    vat_number: ''
  });

  // Dati dell'amministratore: l'art. 1129 c.c. impone che siano reperibili,
  // e finora non c'era alcun campo dove metterli.
  const AMM_VUOTO = {
    full_name: '', fiscal_code: '', company_name: '', email: '', pec: '',
    phone: '', address: '', appointed_on: '', accepted_on: '',
    term_expires_on: '', notes: '',
  };
  const condominio = selectedCondominium?.condominium;
  const isAdmin = selectedCondominium?.role === 'ADMIN';
  const [amministratore, setAmministratore] = useState(AMM_VUOTO);
  const [salvandoAmm, setSalvandoAmm] = useState(false);

  useEffect(() => {
    setAmministratore({ ...AMM_VUOTO, ...(condominio?.administrator || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominio?.id, condominio?.administrator]);

  const salvaAmministratore = async (e) => {
    e.preventDefault();
    if (!condominio?.id) return;
    setSalvandoAmm(true);
    try {
      const ripulito = Object.fromEntries(
        Object.entries(amministratore).map(([k, v]) => [k, v === '' ? null : v])
      );
      await axios.put(`${API}/condominiums/${condominio.id}`, {
        name: condominio.name,
        address: condominio.address,
        city: condominio.city,
        postal_code: condominio.postal_code,
        vat_number: condominio.vat_number || null,
        administrator: ripulito,
      });
      toast.success("Dati dell'amministratore salvati");
      await refreshCondominiums();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSalvandoAmm(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/condominiums`, formData);
      toast.success('Condominio creato con successo!');
      setOpenCreateDialog(false);
      setFormData({ name: '', address: '', city: '', postal_code: '', vat_number: '' });
      await refreshCondominiums();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PageHeader
        title="Impostazioni"
        subtitle="Configurazione del condominio"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Card */}
        <Card className="border-stone-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-semibold text-slate-900">
              Profilo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Nome</p>
              <p className="text-sm text-stone-600">{user?.full_name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Email</p>
              <p className="text-sm text-stone-600">{user?.email}</p>
            </div>
            {user?.fiscal_code && (
              <div>
                <p className="text-sm font-medium text-slate-900">Codice Fiscale</p>
                <p className="text-sm text-stone-600">{user.fiscal_code}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Condominiums Card */}
        <Card className="border-stone-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-heading font-semibold text-slate-900">
              I Miei Condomini
            </CardTitle>
            <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="create-condominium-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crea Nuovo Condominio</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome Condominio *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Condominio Rossi"
                      required
                      data-testid="condo-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Indirizzo *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Via Roma 123"
                      required
                      data-testid="condo-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">Città *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="Milano"
                        required
                        data-testid="condo-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">CAP *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        placeholder="20100"
                        required
                        data-testid="condo-cap-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="vat_number">Partita IVA (opzionale)</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({...formData, vat_number: e.target.value})}
                      placeholder="01234567890"
                      data-testid="condo-vat-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-create-condo">
                    {loading ? 'Creazione...' : 'Crea Condominio'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {condominiums.length === 0 ? (
              <p className="text-sm text-stone-600">Nessun condominio. Crea il primo!</p>
            ) : (
              <div className="space-y-3">
                {condominiums.map((condo) => (
                  <div key={condo.condominium.id} className="flex items-center gap-3 p-3 border border-stone-200 rounded-md" data-testid={`condo-item-${condo.condominium.id}`}>
                    <Building2 className="h-5 w-5 text-slate-900" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{condo.condominium.name}</p>
                      <p className="text-xs text-stone-600">{condo.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dati dell'amministratore — art. 1129 c.c. */}
      {condominio && (
        <Card className="border-stone-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-stone-600" />
              <CardTitle className="text-xl font-heading font-semibold text-slate-900">
                Dati dell&apos;amministratore
              </CardTitle>
            </div>
            <p className="text-sm text-stone-600 mt-1">
              Nome, recapiti, luogo dove si trovano i registri e date dell&apos;incarico.
              L&apos;art. 1129 del codice civile impone che siano reperibili dai condòmini.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={salvaAmministratore} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amm_nome">Nome e cognome</Label>
                  <Input
                    id="amm_nome"
                    value={amministratore.full_name || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, full_name: e.target.value })}
                    disabled={!isAdmin}
                    placeholder="Arch. Mario Rossi"
                    data-testid="administrator-name"
                  />
                </div>
                <div>
                  <Label htmlFor="amm_cf">Codice fiscale</Label>
                  <Input
                    id="amm_cf"
                    value={amministratore.fiscal_code || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, fiscal_code: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_studio">Studio / società</Label>
                  <Input
                    id="amm_studio"
                    value={amministratore.company_name || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, company_name: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_tel">Telefono</Label>
                  <Input
                    id="amm_tel"
                    value={amministratore.phone || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, phone: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_email">Email</Label>
                  <Input
                    id="amm_email"
                    type="email"
                    value={amministratore.email || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, email: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_pec">PEC</Label>
                  <Input
                    id="amm_pec"
                    value={amministratore.pec || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, pec: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="amm_indirizzo">
                  Locali dove si trovano registri e documentazione
                </Label>
                <Input
                  id="amm_indirizzo"
                  value={amministratore.address || ''}
                  onChange={(e) =>
                    setAmministratore({ ...amministratore, address: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="Via ... n. ... - CAP Città"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="amm_nomina">Data di nomina</Label>
                  <Input
                    id="amm_nomina"
                    type="date"
                    value={amministratore.appointed_on || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, appointed_on: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_accettazione">Accettazione dell&apos;incarico</Label>
                  <Input
                    id="amm_accettazione"
                    type="date"
                    value={amministratore.accepted_on || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, accepted_on: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <Label htmlFor="amm_scadenza">Scadenza dell&apos;incarico</Label>
                  <Input
                    id="amm_scadenza"
                    type="date"
                    value={amministratore.term_expires_on || ''}
                    onChange={(e) =>
                      setAmministratore({ ...amministratore, term_expires_on: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="amm_note">Note</Label>
                <Textarea
                  id="amm_note"
                  rows={2}
                  value={amministratore.notes || ''}
                  onChange={(e) =>
                    setAmministratore({ ...amministratore, notes: e.target.value })}
                  disabled={!isAdmin}
                />
              </div>

              {isAdmin && (
                <div className="flex justify-end">
                  <Button type="submit" disabled={salvandoAmm}>
                    {salvandoAmm ? 'Salvataggio…' : 'Salva dati amministratore'}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {/* User Management Section (Only for ADMIN) */}
      {selectedCondominium?.role === 'ADMIN' && selectedCondominium?.condominium && (
        <UserManagement condominiumId={selectedCondominium.condominium.id} />
      )}
    </div>
  );
};